import { useCallback, useEffect, useRef } from 'react';
import { VideoJsPlayer } from 'video.js';
import { getTimeAtFrame } from '@helpers/avInfo';
import { useAssetViewerState } from '@hooks/useAssetViewerState';

type useKeysProps = {
  duration: number;
  isPaused: boolean;
  player: VideoJsPlayer;
  isLooped: boolean;
  onFastForward: (v: number) => void;
  setPlaybackSpeed: (v: number) => void;
};
export default function useKeys({
  onFastForward,
  setPlaybackSpeed,
  duration,
  isLooped,
  isPaused,
  player
}: useKeysProps) {
  const speedRef = useRef(0);
  const reverseTargetTimeRef = useRef<number | null>(null);
  const isReverseRunningRef = useRef(false);
  const {
    isCommentFieldFocused,
    selectedVersion,
    commentsList,
    selectedCommentId,
    selectComment
  } = useAssetViewerState();

  const intervalRewind = useRef<number>(0);
  const EDGE_EPSILON = 0.001;
  const VOLUME_STEP = 0.1;

  const stopReversePlayback = useCallback(() => {
    cancelAnimationFrame(intervalRewind.current);
    isReverseRunningRef.current = false;
    reverseTargetTimeRef.current = null;
    speedRef.current = 0;
    setPlaybackSpeed(1);
    player.playbackRate(1);
  }, [player, setPlaybackSpeed]);

  const seekBySeconds = useCallback(
    (seconds: number) => {
      stopReversePlayback();
      onFastForward(seconds);
    },
    [onFastForward, stopReversePlayback]
  );

  const seekByFrame = useCallback(
    (direction: -1 | 1) => {
      stopReversePlayback();
      const metadata = selectedVersion?.metadata;
      const timePerFrame = getTimeAtFrame(1, metadata) ?? 1;
      const baseTime = player.currentTime();
      const newTime = Math.max(0, baseTime + direction * timePerFrame);
      const clampedTime = Math.max(0, Math.min(duration, newTime));
      player.pause();
      player.currentTime(clampedTime);
    },
    [duration, player, selectedVersion?.metadata, stopReversePlayback]
  );

  const toggleMute = useCallback(() => {
    const currentVolume = player.volume();
    player.volume(currentVolume ? 0 : 0.5);
  }, [player]);

  const stepVolume = useCallback(
    (direction: -1 | 1) => {
      const currentVolume = player.volume();
      const nextVolume = Math.max(
        0,
        Math.min(1, currentVolume + direction * VOLUME_STEP)
      );
      player.volume(nextVolume);
    },
    [player]
  );

  const toggleFullscreen = useCallback(() => {
    if (player.isFullscreen()) player.exitFullscreen();
    else player.requestFullscreen();
  }, [player]);

  const goToVisualComment = useCallback(
    (direction: -1 | 1) => {
      const visualComments = commentsList
        .filter(
          (c) => typeof c.timeCode?.fromSeconds === 'number' && !c.isCopied
        )
        .sort((a, b) => a.timeCode!.fromSeconds - b.timeCode!.fromSeconds);
      if (visualComments.length === 0) return;

      const currentTime = player.currentTime();
      const selectedIndex = visualComments.findIndex(
        (c) => c.id === selectedCommentId
      );

      if (selectedIndex === -1) {
        const isForward = direction > 0;
        const nearest = isForward
          ? visualComments.find((c) => c.timeCode!.fromSeconds > currentTime) ??
            visualComments[0]
          : [...visualComments]
              .reverse()
              .find((c) => c.timeCode!.fromSeconds < currentTime) ??
            visualComments.at(-1)!;
        selectComment(nearest.id);
        return;
      }

      const nextIndex =
        (selectedIndex + direction + visualComments.length) %
        visualComments.length;

      selectComment(visualComments[nextIndex].id);
    },
    [commentsList, player, selectComment, selectedCommentId]
  );

  const onReverseSeek = (time: number) => {
    if (speedRef.current >= 0) return;
    reverseTargetTimeRef.current = time;
    player.currentTime(time);
  };

  const onExternalPlaybackRateChange = useCallback(
    (value: number) => {
      cancelAnimationFrame(intervalRewind.current);
      isReverseRunningRef.current = false;
      reverseTargetTimeRef.current = null;
      const wasReverse = speedRef.current < 0;
      speedRef.current = value;
      setPlaybackSpeed(value);
      player.playbackRate(value);
      if (wasReverse) {
        player.play();
      }
    },
    [player, setPlaybackSpeed]
  );

  const KeyL = useCallback(
    (multiplier: number) => {
      cancelAnimationFrame(intervalRewind.current);
      isReverseRunningRef.current = false;
      reverseTargetTimeRef.current = null;
      speedRef.current = multiplier;
      setPlaybackSpeed(multiplier);
      player.playbackRate(multiplier);
      player.play();
    },
    [player, setPlaybackSpeed]
  );

  const KeyJ = useCallback(
    (multiplier: number) => {
      speedRef.current = multiplier;
      isReverseRunningRef.current = true;
      setPlaybackSpeed(multiplier);

      player.pause();

      let lastTimestamp: number | null = null;
      const startTime = player.currentTime();
      const reverseStartTime =
        startTime <= EDGE_EPSILON && duration > 0 ? duration : startTime;
      reverseTargetTimeRef.current = reverseStartTime;
      player.currentTime(reverseStartTime);

      cancelAnimationFrame(intervalRewind.current);
      const play = (timestamp: number) => {
        if (!isReverseRunningRef.current) return;
        if (lastTimestamp === null) {
          lastTimestamp = timestamp;
          intervalRewind.current = requestAnimationFrame(play);
          return;
        }

        const elapsedSeconds = (timestamp - lastTimestamp) / 1000;
        lastTimestamp = timestamp;

        const currentVirtualTime =
          reverseTargetTimeRef.current ?? player.currentTime();
        let virtualTime =
          currentVirtualTime - elapsedSeconds * Math.abs(multiplier);
        reverseTargetTimeRef.current = virtualTime;

        if (virtualTime <= 0) {
          if (isLooped) {
            virtualTime = duration;
            reverseTargetTimeRef.current = duration;
            player.currentTime(duration);
          } else {
            isReverseRunningRef.current = false;
            reverseTargetTimeRef.current = 0;
            player.currentTime(0);
            cancelAnimationFrame(intervalRewind.current);
            return;
          }
        }

        if (!player.seeking()) {
          player.currentTime(virtualTime);
        }

        intervalRewind.current = requestAnimationFrame(play);
      };
      intervalRewind.current = requestAnimationFrame(play);
    },
    [duration, isLooped, player, setPlaybackSpeed]
  );

  const playPause = useCallback(() => {
    if (isPaused) {
      if (speedRef.current < 0) {
        const baseTime = reverseTargetTimeRef.current ?? player.currentTime();
        const targetTime =
          baseTime <= EDGE_EPSILON && duration > 0 ? duration : baseTime;

        if (isReverseRunningRef.current) {
          cancelAnimationFrame(intervalRewind.current);
          isReverseRunningRef.current = false;
          player.currentTime(targetTime);
          return;
        }

        player.currentTime(targetTime);
        KeyJ(speedRef.current);
        return;
      }
      const currentRate = player.playbackRate() || 1;
      player.playbackRate(currentRate);
      setPlaybackSpeed(currentRate);
      player.play();
      speedRef.current = currentRate;
    } else {
      player.pause();
    }
  }, [duration, isPaused, player, setPlaybackSpeed, KeyJ]);

  useEffect(() => {
    if (isCommentFieldFocused) return;

    const onKeyPress = (e: any) => {
      const modalElement = document.querySelector('.ant-modal-root');
      const fieldElement = document.querySelector(
        '.filter-sort-panel-search-input'
      );
      const transcriptSearchInput = document.getElementById(
        'TRANSCRIPT_SEARCH_INPUT'
      );

      if (modalElement || fieldElement || transcriptSearchInput) return;

      const target = e.target as HTMLElement | null;
      const isTypingTarget =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if (isTypingTarget) return;

      if (e.code === 'ArrowLeft') {
        e.preventDefault();
        seekBySeconds(-5);
        return;
      }
      if (e.code === 'ArrowRight') {
        e.preventDefault();
        seekBySeconds(5);
        return;
      }
      if (e.code === 'Comma') {
        e.preventDefault();
        seekByFrame(-1);
        return;
      }
      if (e.code === 'Period') {
        e.preventDefault();
        seekByFrame(1);
        return;
      }
      if (e.code === 'KeyK') {
        e.preventDefault();
        stopReversePlayback();
        player.pause();
        return;
      }
      if (e.code === 'Space') {
        e.preventDefault();
        playPause();
        return;
      }
      if (e.code === 'KeyF') {
        e.preventDefault();
        toggleFullscreen();
        return;
      }
      if (e.code === 'KeyM') {
        e.preventDefault();
        toggleMute();
        return;
      }
      if (e.code === 'Minus') {
        e.preventDefault();
        stepVolume(-1);
        return;
      }
      if (e.code === 'Equal') {
        e.preventDefault();
        stepVolume(1);
        return;
      }
      if (e.shiftKey && e.code === 'KeyP') {
        e.preventDefault();
        goToVisualComment(-1);
        return;
      }
      if (e.shiftKey && e.code === 'KeyN') {
        e.preventDefault();
        goToVisualComment(1);
        return;
      }
      if (e.code === 'KeyJ') {
        e.preventDefault();
        const speed = speedRef.current;
        if (speed > 1) {
          KeyL(speed - 1);
          return;
        }
        if (speed === 1) {
          KeyJ(-1);
          return;
        }
        const _speed = speed - 1 >= -8 ? speed - 1 || -1 : speed;
        KeyJ(_speed);
        return;
      }
      if (e.code === 'KeyL') {
        e.preventDefault();
        const speed = speedRef.current;
        if (speed < -1) {
          KeyJ(speed + 1);
          return;
        }
        if (speed === -1) {
          KeyL(1);
          return;
        }
        const _speed = speed + 1 > 8 ? speed : speed + 1 || 1;
        KeyL(_speed);
      }
    };

    document.addEventListener('keydown', onKeyPress);

    return () => {
      document.removeEventListener('keydown', onKeyPress);
    };
  }, [
    duration,
    isCommentFieldFocused,
    isLooped,
    isPaused,
    onFastForward,
    player,
    selectedVersion?.metadata,
    setPlaybackSpeed,
    playPause,
    KeyJ,
    KeyL,
    goToVisualComment,
    seekByFrame,
    seekBySeconds,
    stepVolume,
    stopReversePlayback,
    toggleFullscreen,
    toggleMute
  ]);

  return {
    speedRef,
    isReverseRunningRef,
    playPause,
    onReverseSeek,
    onExternalPlaybackRateChange
  };
}
